import { Injectable } from '@angular/core';
import { User } from '../user/user.model';

declare global {
  interface Window {
    clarity?: (...args: any[]) => void;
  }
}

// Provided globally by the cordova-clarity plugin (js-module clobbers "ClarityPlugin").
// Only present on native builds - see AppComponent.initializeClarity().
declare let ClarityPlugin: any;

interface ClarityIdentity {
  user: User;
  pathId?: number | string;
  clientId?: number | string;
}

// Tags the current Microsoft Clarity session with the logged-in user, path, and
// client so recordings can be filtered by email/user id/path id/client id in the
// Clarity dashboard. See the web snippet in src/index.html that defines window.clarity,
// and AppComponent.initializeClarity() for the native (Cordova) session.
//
// clientId/pathId are passed as the identify() customSessionId/customPageId slots
// (Filters > Custom session ID / Custom page ID) rather than only as `set` tags,
// because those two are plain exact-match filters available immediately - `set` tags
// only appear in the Filters > Custom tags picker once Clarity indexes the tag key,
// which can lag well behind the session actually being recorded.
@Injectable({
  providedIn: 'root',
})
export class ClarityService {
  private nativeReady = false;
  private pendingNativeIdentity: ClarityIdentity | null = null;

  identify(user: User, pathId?: number | string, clientId?: number | string) {
    this.identifyWeb(user, pathId, clientId);
    this.identifyNative(user, pathId, clientId);
  }
  markNativeReady() {
    this.nativeReady = true;
    if (this.pendingNativeIdentity) {
      const { user, pathId, clientId } = this.pendingNativeIdentity;
      this.pendingNativeIdentity = null;
      this.tagNative(user, pathId, clientId);
    }
  }

  private identifyWeb(user: User, pathId?: number | string, clientId?: number | string) {
    try {
      if (typeof window === 'undefined' || !window.clarity || !user?.email) {
        return;
      }
      const friendlyName = [user.firstName, user.lastName].filter(Boolean).join(' ');
      window.clarity(
        'identify',
        user.email,
        clientId ? String(clientId) : null,
        pathId ? String(pathId) : null,
        friendlyName
      );
      window.clarity('set', 'userEmail', user.email);
      window.clarity('set', 'kyperUserId', String(user.id));
      if (pathId) {
        window.clarity('set', 'pathId', String(pathId));
      }
      if (clientId) {
        window.clarity('set', 'clientId', String(clientId));
      }
    } catch (error) {
      console.log('Clarity identify failed', error);
    }
  }

  private identifyNative(user: User, pathId?: number | string, clientId?: number | string) {
    if (typeof ClarityPlugin === 'undefined' || !user?.email) {
      return;
    }
    if (!this.nativeReady) {
      this.pendingNativeIdentity = { user, pathId, clientId };
      return;
    }
    this.tagNative(user, pathId, clientId);
  }

  private tagNative(user: User, pathId?: number | string, clientId?: number | string) {
    try {
      ClarityPlugin.setCustomUserId(user.email, () => {}, (error: any) => {
        console.error('Clarity setCustomUserId failed', error);
      });
      ClarityPlugin.setCustomTag('kyperUserId', String(user.id), () => {}, (error: any) => {
        console.error('Clarity setCustomTag (kyperUserId) failed', error);
      });
      if (clientId) {
        ClarityPlugin.setCustomSessionId(String(clientId), () => {}, (error: any) => {
          console.error('Clarity setCustomSessionId failed', error);
        });
        ClarityPlugin.setCustomTag('clientId', String(clientId), () => {}, (error: any) => {
          console.error('Clarity setCustomTag (clientId) failed', error);
        });
      }
      if (pathId) {
        ClarityPlugin.setCustomTag('pathId', String(pathId), () => {}, (error: any) => {
          console.error('Clarity setCustomTag (pathId) failed', error);
        });
      }
    } catch (error) {
      console.log('Clarity native identify failed', error);
    }
  }
}
